import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Slider,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ApiService from '../../services/api-service';
import notifier from '../Notifier';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiBackdrop-root': {
    backgroundColor: 'rgba(0, 0, 0, 0.8)'
  },
  '& .MuiDialog-paper': {
    backgroundColor: '#000000',
    color: '#ffffff',
    borderRadius: '16px',
    minWidth: '400px',
    maxWidth: '500px',
    border: '0.5px solid #FFEE20'
  }
}));

const StreamPurchaseDialog = ({ open, onClose, currentStreams = 0, onPurchaseComplete }) => {
  const [streamCount, setStreamCount] = useState(100);
  const paymentMethod = 'siki'; // Always use SIKI for stream purchases
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [userBalances, setUserBalances] = useState({});

  // Fetch pricing when stream count changes
  useEffect(() => {
    if (open && streamCount > 0) {
      fetchPricing();
    }
  }, [streamCount, open]);

  // Fetch user balances when dialog opens
  useEffect(() => {
    if (open) {
      fetchUserBalances();
    }
  }, [open]);

  const fetchPricing = async () => {
    try {
      setLoading(true);
      const response = await ApiService.get(`/stream-purchase/pricing?streamCount=${streamCount}&paymentMethod=${paymentMethod}`);
      setPricing(response.result);
    } catch (error) {
      console.error('Failed to fetch pricing:', error);
      notifier.error('Failed to calculate pricing');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserBalances = async () => {
    try {
      const response = await ApiService.BALANCE();
      console.log('Balance API response:', response);
      if (response.result?.wallets) {
        setUserBalances({
          siki: response.result.wallets[0]?.balance || 0,
          hbar: response.result.wallets[2]?.balance || 0,
          matic: response.result.wallets[1]?.balance || 0,
          near: response.result.wallets[3]?.balance || 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch user balances:', error);
    }
  };

  const handlePurchase = async () => {
    try {
      setPurchasing(true);
      
      const response = await ApiService.post('/stream-purchase/purchase', {
        streamCount,
        paymentMethod
      });

      notifier.success(`Successfully purchased ${streamCount} streams!`);
      
      if (onPurchaseComplete) {
        onPurchaseComplete(response.result);
      }
      
      onClose();
    } catch (error) {
      console.error('Purchase failed:', error);
      const errorMessage = error.response?.data?.error || 'Failed to purchase streams';
      notifier.error(errorMessage);
    } finally {
      setPurchasing(false);
    }
  };

  const hasInsufficientBalance = () => {
    if (!pricing || !userBalances.siki) return false;
    return userBalances.siki < pricing.finalCost;
  };

  const getBalanceForPaymentMethod = () => {
    console.log('Current userBalances:', userBalances);
    console.log('SIKI balance:', userBalances.siki);
    return userBalances.siki || 0;
  };

  const getCurrencySymbol = () => {
    return 'SIKI';
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ backgroundColor: '#000000' }}>
        <Typography variant="h6" sx={{ color: '#FFEE20', fontWeight: 'bold' }}>
          Purchase Additional Streams
        </Typography>
        <Typography variant="body2" sx={{ color: '#FFEE20', mt: 1 }}>
          Current streaming balance: {currentStreams} streams
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: '#000000' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2, backgroundColor: '#000000' }}>
          {/* Stream Count Selection */}
          <Box sx={{ backgroundColor: '#000000' }}>
            <Typography variant="body1" sx={{ mb: 2, fontWeight: 'bold' }}>
              How many streams would you like to purchase?
            </Typography>
            
            <Box sx={{ px: 1, mb: 4 }}>
              <Slider
                value={streamCount}
                onChange={(e, newValue) => setStreamCount(newValue)}
                min={1}
                max={1000}
                step={10}
                marks={[
                  { value: 50, label: '50' },
                  { value: 100, label: '100' },
                  { value: 250, label: '250' },
                  { value: 500, label: '500' },
                  { value: 1000, label: '1000' }
                ]}
                sx={{
                  color: '#FFEE20',
                  '& .MuiSlider-mark': { color: '#FFEE20' },
                  '& .MuiSlider-markLabel': { 
                    color: '#FFEE20',
                    fontSize: '12px'
                  },
                  '& .MuiSlider-thumb': {
                    backgroundColor: '#FFEE20',
                    border: '2px solid #FFEE20'
                  },
                  '& .MuiSlider-track': {
                    backgroundColor: '#FFEE20'
                  },
                  '& .MuiSlider-rail': {
                    backgroundColor: '#000000'
                  }
                }}
              />
            </Box>
            
            <TextField
              value={streamCount}
              onChange={(e) => setStreamCount(Math.max(1, Math.min(10000, parseInt(e.target.value) || 1)))}
              type="number"
              fullWidth
              sx={{
                mt: 1,
                '& .MuiOutlinedInput-root': {
                  color: '#ffffff',
                  backgroundColor: '#000000',
                  '& fieldset': { borderColor: '#FFEE20', borderWidth: '0.5px' },
                  '&:hover fieldset': { borderColor: '#FFEE20', borderWidth: '0.5px' },
                  '&.Mui-focused fieldset': { borderColor: '#FFEE20', borderWidth: '0.5px' }
                },
                '& .MuiInputLabel-root': { color: '#FFEE20' }
              }}
              label="Stream Count"
              inputProps={{ min: 1, max: 10000 }}
            />
          </Box>

          {/* Show current SIKI balance */}
          <Typography variant="body2" sx={{ color: '#FFEE20', mb: 2 }}>
            Your SIKI balance: {getBalanceForPaymentMethod().toFixed(6)}
          </Typography>

          {/* Pricing Breakdown */}
          {pricing && !loading && (
            <Box sx={{ 
              backgroundColor: '#000000', 
              padding: 2, 
              borderRadius: 2,
              border: hasInsufficientBalance() ? '0.5px solid #f44336' : '0.5px solid #FFEE20'
            }}>
              <Typography variant="h6" sx={{ color: '#FFEE20', mb: 2 }}>
                Pricing Breakdown
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Base cost ({streamCount} streams × $0.01):</Typography>
                <Typography>${pricing.baseCostUSD}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Platform fee (4%):</Typography>
                <Typography>${pricing.platformFeeUSD}</Typography>
              </Box>
              
              <Divider sx={{ my: 1, backgroundColor: '#FFEE20' }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography>Total (USD):</Typography>
                <Typography>${pricing.totalCostUSD}</Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                <Typography sx={{ color: '#FFEE20' }}>
                  Final cost:
                </Typography>
                <Typography sx={{ color: '#FFEE20' }}>
                  {pricing.finalCost.toFixed(6)} {pricing.currencySymbol}
                </Typography>
              </Box>

              {hasInsufficientBalance() && (
                <Alert severity="error" sx={{ mt: 2, backgroundColor: '#000000', border: '0.5px solid #f44336', color: '#f44336' }}>
                  Insufficient {getCurrencySymbol()} balance. You need {pricing.finalCost.toFixed(6)} but only have {getBalanceForPaymentMethod().toFixed(6)}.
                </Alert>
              )}
            </Box>
          )}

          {loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, backgroundColor: '#000000' }}>
              <CircularProgress size={24} sx={{ color: '#FFEE20' }} />
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: 3, backgroundColor: '#000000' }}>
        <Button
          onClick={onClose}
          sx={{ 
            color: '#FFEE20',
            border: '0.5px solid #FFEE20',
            '&:hover': { backgroundColor: '#1a1a1a', borderColor: '#FFEE20', borderWidth: '0.5px' }
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handlePurchase}
          disabled={!pricing || loading || purchasing || hasInsufficientBalance()}
          sx={{
            backgroundColor: '#FFEE20',
            color: '#000',
            fontWeight: 'bold',
            '&:hover': { backgroundColor: '#FFE000' },
            '&:disabled': { backgroundColor: '#000000', color: '#666666', border: '0.5px solid #333333' }
          }}
        >
          {purchasing ? (
            <>
              <CircularProgress size={16} sx={{ mr: 1, color: '#000' }} />
              Processing...
            </>
          ) : (
            'Purchase Streams'
          )}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default StreamPurchaseDialog;